import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Geri butonu — string ise `navigate(string)`, fonksiyon ise doğrudan çağrılır. */
  backTo?: string | (() => void);
  /** Sağa hizalı aksiyonlar (ör. "Yeni X" butonu, dışa aktarma). */
  actions?: ReactNode;
  /** Başlığın yanındaki durum göstergesi (ör. `<StatusChip/>`). */
  status?: ReactNode;
  /** Başlığın altındaki soluk küçük satır. */
  meta?: ReactNode;
}

/** Tek tip sayfa başlığı — 63 dosyadaki elle yazılmış `<Typography variant="h5">` +
 *  3 farklı geri-buton stilini birleştirir. */
export function PageHeader({ title, subtitle, backTo, actions, status, meta }: PageHeaderProps) {
  const navigate = useNavigate();
  const handleBack = () => {
    if (typeof backTo === 'function') backTo();
    else if (typeof backTo === 'string') navigate(backTo);
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between', gap: 1.5, mb: 3 }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
        {backTo != null && (
          <IconButton onClick={handleBack} size="small" aria-label="Geri" sx={{ ml: -0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h5" component="h1" noWrap sx={{ maxWidth: '100%' }}>
              {title}
            </Typography>
            {status}
          </Stack>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
          {meta && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
              {meta}
            </Typography>
          )}
        </Box>
      </Stack>
      {actions && (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap' }}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}
